import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../../../services/auth.service';
import { UserService } from '../../../../services/user.service';
import { TranslationService } from '../services/translation.service';

@Injectable({ providedIn: 'root' })
export class I18nStore {
  private readonly translationService = inject(TranslationService);
  private readonly authService = inject(AuthService);
  private readonly userService = inject(UserService);

  // Signals for reactive state
  readonly lang = signal<string>('de');
  readonly loading = signal<boolean>(false);
  readonly offline = signal<boolean>(false);
  readonly lastUpdated = signal<Date | null>(null);

  // Translation cache - loaded from database
  private readonly translations = signal<Record<string, Record<string, string>>>({});

  // Available languages from database
  readonly availableLanguages = signal<string[]>(['de', 'en', 'fr']);

  // Flag to prevent multiple initialization
  private initialized = signal<boolean>(false);

  /**
   * Guard flag to prevent Effect from running during user-initiated language changes.
   */
  private _isUserSelection = false;

  // Current dictionary (computed from lang and translations)
  readonly dict = computed(() => {
    const currentLang = this.lang();
    const allTranslations = this.translations();

    // Use DB translations if available
    if (allTranslations[currentLang] && Object.keys(allTranslations[currentLang]).length > 0) {
      return allTranslations[currentLang];
    }

    // Return empty object if DB not available
    return {};
  });

  constructor() {
    // Load saved language from localStorage (fallback)
    const savedLang = localStorage.getItem('app-language');
    if (savedLang) {
      this.lang.set(savedLang);
    }

    // Save language changes to localStorage
    effect(() => {
      localStorage.setItem('app-language', this.lang());
    });

    // Load language from User Settings when authenticated
    effect(() => {
      const isAuthenticated = this.authService.isAuthenticated();

      if (this._isUserSelection) {
        console.log('🛡️ I18nStore Effect: Skipping (user-initiated change)');
        return;
      }

      // Only initialize if not already initialized and user is authenticated
      if (isAuthenticated && !this.initialized()) {
        this.initializeFromDatabase();
      } else if (!isAuthenticated) {
        // Clear translations on logout
        this.translations.set({});
        this.offline.set(true);
        this.initialized.set(false);
      }
    });

    // Monitor offline status based on available translations
    effect(() => {
      const currentLang = this.lang();
      const allTranslations = this.translations();
      const isLoading = this.loading();

      // Set offline if not loading and no translations available
      if (
        !isLoading &&
        (!allTranslations[currentLang] || Object.keys(allTranslations[currentLang]).length === 0)
      ) {
        this.offline.set(true);
      } else if (
        !isLoading &&
        allTranslations[currentLang] &&
        Object.keys(allTranslations[currentLang]).length > 0
      ) {
        this.offline.set(false);
      }
    });
  }

  /**
   * Initialize translations from database
   */
  private async initializeFromDatabase(): Promise<void> {
    if (this.initialized()) {
      return;
    }

    this.loading.set(true);
    this.initialized.set(true);

    try {
      //  Load contact info to get preferred language
      let contact = this.userService.getContactFromService();

      // If contact not loaded yet, load it automatically
      if (!contact || !contact.contact_id) {
        // Get user email from auth token
        const userInfo = this.authService.getUserInfo();
        const userEmail = userInfo?.email || userInfo?.['sub'];

        if (userEmail) {
          try {
            contact = await this.userService.loadContactByEmail(userEmail);
          } catch (error) {
            console.warn('⚠️ I18nStore: Could not load contact, will use default language');
          }
        }
      }

      if (contact?.fk_language_id) {
        // Map language ID to language code
        const languageMapping: Record<number, string> = {
          2: 'sq', // Albanian
          3: 'ar', // Arabic
          14: 'en', // English
          17: 'fr', // French
          19: 'de', // German
          37: 'pt', // Portuguese
          42: 'es', // Spanish
        };

        const langCode = languageMapping[contact.fk_language_id];
        if (langCode) {
          this.lang.set(langCode);
        } else {
          console.warn(
            `⚠️ I18nStore: Unknown language_id ${contact.fk_language_id}, using default`,
          );
        }
      }

      const currentLang = this.lang();
      await this.loadSpecificLanguage(currentLang);
    } catch (error) {
      console.error('❌ I18n Store: Failed to initialize translations from database:', error);
      this.offline.set(true);
      this.initialized.set(false);
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Load translations for a specific language
   */
  private async loadSpecificLanguage(langCode: string): Promise<void> {
    const currentTranslations = this.translations();

    if (currentTranslations[langCode] && Object.keys(currentTranslations[langCode]).length > 0) {
      return;
    }

    try {
      const translations = await this.translationService
        .getTranslations(langCode, 1001)
        .toPromise();

      if (translations && Object.keys(translations).length > 0) {
        this.translations.update((current) => ({
          ...current,
          [langCode]: translations,
        }));
        this.lastUpdated.set(new Date());
      } else {
        console.warn(`⚠️ I18n Store: No translations found for ${langCode} in database`);
        this.translations.update((current) => ({
          ...current,
          [langCode]: {},
        }));
      }
    } catch (error) {
      console.error(`❌ I18n Store: Failed to load translations for ${langCode}:`, error);
      this.translations.update((current) => ({
        ...current,
        [langCode]: {},
      }));
      throw error;
    }
  }

  /**
   * Reload translations from database
   * Used by APP_INITIALIZER to ensure translations are loaded before app starts
   */
  async refreshTranslations(): Promise<void> {
    // Reset initialization flag
    this.initialized.set(false);

    // Force re-initialization
    await this.initializeFromDatabase();
  }

  /**
   * Load translations for specific language
   */
  async loadLanguage(langCode: string): Promise<void> {
    if (!this.availableLanguages().includes(langCode)) {
      console.warn(`Language ${langCode} not available`);
      return;
    }

    this.loading.set(true);

    try {
      await this.loadSpecificLanguage(langCode);
      this.offline.set(false);
    } catch (error) {
      console.error(`❌ Failed to load ${langCode} translations:`, error);
      this.offline.set(true);
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Set active language
   * This will:
   * 1. Update the UI language immediately (local)
   * 2. Persist the preference to database via Contact.fk_language_id
   */
  async setLanguage(langCode: string): Promise<void> {
    if (!this.availableLanguages().includes(langCode)) {
      console.error(`❌ Language ${langCode} not available. Available:`, this.availableLanguages());
      return;
    }

    try {
      // Set guard flag to prevent Effect from running during user action
      this._isUserSelection = true;
      console.log(`👤 I18nStore.setLanguage(): User changing language to ${langCode}`);

      // 1. Update UI immediately for better UX
      this.lang.set(langCode);
      await this.loadLanguage(langCode);

      // 2. Get language ID from hardcoded mapping
      const languageMapping: Record<string, number> = {
        sq: 2, // Albanian
        ar: 3, // Arabic
        en: 14, // English
        fr: 17, // French
        de: 19, // German
        pt: 37, // Portuguese
        es: 42, // Spanish
      };

      const languageId = languageMapping[langCode];
      if (!languageId) {
        console.error(`❌ Could not find language ID for ${langCode}`);
        this._isUserSelection = false;
        return;
      }

      // 3. Persist to database if user is authenticated
      if (this.authService.isAuthenticated()) {
        await this.userService.updateUserLanguage(languageId);
      } else {
      }

      // Reset guard flag after DB save completes
      setTimeout(() => {
        this._isUserSelection = false;
        console.log(`🔓 I18nStore: Guard flag reset, language change complete`);
      }, 500);
    } catch (error) {
      console.error(`❌ Failed to set language ${langCode}:`, error);
      this._isUserSelection = false;
      throw error;
    }
  }

  // Local fallback dictionary for portal keys not yet inserted into DB
  private static readonly FALLBACK_TRANSLATIONS: Record<string, Record<string, string>> = {
    de: {
      'portal.degrees.attendance.title': 'Anwesenheit',
      'portal.degrees.attendance.summary_title': 'Anwesenheitsübersicht',
      'portal.degrees.attendance.attended': 'Anwesend',
      'portal.degrees.attendance.absent': 'Abwesend',
      'portal.degrees.attendance.rate': 'Quote',
      'portal.degrees.attendance.lectures_short': 'Lekt.',
      'portal.degrees.attendance.no_data': 'Keine Daten',
      'portal.welcome_openolat': 'Willkommen auf der OpenOlat Lernplattform von {{school}}',
      'finance.status.paused': 'Pausiert',
      'finance.status.aborted': 'Abgebrochen',
    },
    en: {
      'portal.degrees.attendance.title': 'Attendance',
      'portal.degrees.attendance.summary_title': 'Attendance Summary',
      'portal.degrees.attendance.attended': 'Attended',
      'portal.degrees.attendance.absent': 'Absent',
      'portal.degrees.attendance.rate': 'Rate',
      'portal.degrees.attendance.lectures_short': 'lect.',
      'portal.degrees.attendance.no_data': 'No data',
      'portal.welcome_openolat': 'Welcome to the OpenOlat learning platform of {{school}}',
      'finance.status.paused': 'Paused',
      'finance.status.aborted': 'Aborted',
    },
    fr: {
      'portal.degrees.attendance.title': 'Présence',
      'portal.degrees.attendance.summary_title': 'Résumé des présences',
      'portal.degrees.attendance.attended': 'Présent',
      'portal.degrees.attendance.absent': 'Absent',
      'portal.degrees.attendance.rate': 'Taux',
      'portal.degrees.attendance.lectures_short': 'cours',
      'portal.degrees.attendance.no_data': 'Pas de données',
      'portal.welcome_openolat': 'Bienvenue sur la plateforme d\'apprentissage OpenOlat de {{school}}',
      'finance.status.paused': 'En pause',
      'finance.status.aborted': 'Abandonné',
    },
  };

  /**
   * Translation function - get text for key
   */
  t(key: string, params?: Record<string, string>): string {
    let text = this.dict()[key];

    if (!text) {
      const currentLang = this.lang();
      text = I18nStore.FALLBACK_TRANSLATIONS[currentLang]?.[key] || I18nStore.FALLBACK_TRANSLATIONS['de']?.[key] || key;
    }

    if (!text) {
      return key; // Return key if translation missing
    }

    // Simple parameter replacement
    if (params) {
      Object.entries(params).forEach(([param, value]) => {
        text = text.replace(new RegExp(`{{${param}}}`, 'g'), value);
      });
    }

    return text;
  }

  /**
   * Debug method - get current state
   */
  getDebugInfo(): {
    currentLanguage: string;
    availableLanguages: string[];
    isLoading: boolean;
    isOffline: boolean;
    isInitialized: boolean;
    isAuthenticated: boolean;
    translationCounts: Record<string, number>;
    lastUpdated: Date | null;
  } {
    const allTranslations = this.translations();
    const translationCounts: Record<string, number> = {};

    Object.entries(allTranslations).forEach(([lang, translations]) => {
      translationCounts[lang] = Object.keys(translations).length;
    });

    return {
      currentLanguage: this.lang(),
      availableLanguages: this.availableLanguages(),
      isLoading: this.loading(),
      isOffline: this.offline(),
      isInitialized: this.initialized(),
      isAuthenticated: this.authService.isAuthenticated(),
      translationCounts,
      lastUpdated: this.lastUpdated(),
    };
  }

  /**
   * Force reload translations
   */
  async forceReloadTranslations(): Promise<void> {
    this.initialized.set(false);
    this.translations.set({});
    await this.initializeFromDatabase();
  }
}
